import { ErrorMessage, useField, useFormikContext } from 'formik';

import type { InputProps } from '@src/design-system/input';
import { Input, inputVariants } from '@src/design-system/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/design-system/select';
import { cn } from '@src/design-system/helpers';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { formatTextForDisplay } from '@shared/utils/helpers/format-text-for-display';
import type { ComponentProps } from 'react';

type Props =
  | {
      inputProps: InputProps;
      inputName: string;
      selectPlaceholder?: string;

      children: React.ReactNode;
    }
  | {
      inputProps: InputProps;
      inputName: string;
      selectPlaceholder?: string;

      selectProps?: ComponentProps<typeof Select>;
      selectName: string;
      options: ({ label: string; value: string } | string)[];
    };

export function InputWithSelectField(props: Props) {
  const {
    inputProps: { className, ...iProps },
    selectPlaceholder,
    inputName,
  } = props;
  const { setFieldValue } = useFormikContext();

  const selectProps = 'selectProps' in props ? props.selectProps : undefined;
  const selectName = 'selectName' in props ? props.selectName : undefined;
  const options = 'options' in props ? props.options : undefined;
  const children = 'children' in props ? props.children : undefined;

  const [selectFieldProps, selectFieldMeta] = useField(selectName || '');
  const [inputFieldProps, inputFieldMeta] = useField(inputName);

  return (
    <>
      <div className='grid [&>button]:rounded-l-none [&>button]:border-l-0 grid-cols-[1fr_min-content] w-full'>
        <Input
          hasError={!!inputFieldMeta.touched && !!inputFieldMeta.error}
          className={cn('rounded-r-none flex-1 z-10', className)}
          {...inputFieldProps}
          {...iProps}
        />
        {children ??
          (selectName && options ? (
            <Select
              name={selectName}
              onValueChange={(value) => setFieldValue(selectName, value)}
              value={selectFieldProps.value}
              {...selectProps}
            >
              <SelectTrigger
                hasError={!!selectFieldMeta.touched && !!selectFieldMeta.error}
                className={cn(
                  inputVariants(),
                  'rounded-l-none border-l-0 focus-within:border-l'
                )}
              >
                <SelectValue placeholder={selectPlaceholder} />
              </SelectTrigger>
              <SelectContent align='end'>
                {options.map((opt) => {
                  const isOptString = typeof opt === 'string';

                  const optValue = isOptString ? opt : opt.value;
                  const optLabel = isOptString ? opt : opt.label;

                  if (!optValue) return null;

                  return (
                    <SelectItem
                      key={optValue}
                      value={optValue}
                    >
                      {formatTextForDisplay(optLabel)}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          ) : null)}
      </div>
      {inputFieldProps.name && (
        <ErrorMessage
          name={inputFieldProps.name}
          component={CustomErrorMessage}
        />
      )}
    </>
  );
}
